import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { joinPersonName } from '@/utils/names';
import {
  CheckCircle, XCircle, AlertCircle, DollarSign, FileText, Clock,
  Loader2, CreditCard, ArrowLeft, Plus, X, TrendingUp, Package,
  Scale, ChevronDown, ChevronUp
} from 'lucide-react';
import { api } from '@/utils/api';
import { formatINR, formatDate } from '@/utils/formatters';

// ── Types ───────────────────────────────────────────────────────────────────

interface Deal {
  id: string;
  number: string;
  status: string;
  total: number;
  items: { description: string; quantity: number; rate: number; unit: string }[];
  account: { name: string } | null;
  // C3c (W3): lead-only SOs carry a synthesized contact (L183 fallback) so the
  // picker can show a real customer name when account is null.
  contact: { firstName?: string | null; lastName?: string | null } | null;
  config: Record<string, any>;
  createdAt: string;
  confirmedAt: string | null;
}

interface PaymentStage {
  id: string;
  salesOrderId: string;
  type: string;
  stage: string;
  amount: number;
  paidAmount: number;
  pendingAmount: number;
  status: string;
  dueDate: string | null;
  payments: { amount: number; date: string; mode: string; utr: string }[];
}

type ClosureOption = 'fully_paid' | 'partial_payment' | 'credit_note' | 'debit_note';
type NoteReason = 'shortage' | 'quality_issue' | 'price_dispute' | 'partial_delivery';

// C3c (W3): customer label for the picker — prefer the account name, but fall
// back to the (possibly synthesized L183) contact name when account is null so a
// lead-only SO shows a real person instead of "N/A".
function dealCustomerName(d: Pick<Deal, 'account' | 'contact'>): string {
  const accountName = d.account?.name?.trim();
  if (accountName) return accountName;
  const contactName = joinPersonName(d.contact?.firstName, d.contact?.lastName);
  return contactName || 'N/A';
}

// ── Component ───────────────────────────────────────────────────────────────

export function DealClosurePage() {
  const navigate = useNavigate();

  // Data
  const [deals, setDeals] = useState<Deal[]>([]);
  const [selectedDealId, setSelectedDealId] = useState('');
  const [paymentStages, setPaymentStages] = useState<PaymentStage[]>([]);

  // UI state
  const [loadingDeals, setLoadingDeals] = useState(true);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [closing, setClosing] = useState(false);
  const [creatingNote, setCreatingNote] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState<string | null>(null);

  // Closure form
  const [closureOption, setClosureOption] = useState<ClosureOption>('fully_paid');
  const [partialAmount, setPartialAmount] = useState('');
  const [partialNotes, setPartialNotes] = useState('');

  // CN/DN form
  const [noteReason, setNoteReason] = useState<NoteReason>('shortage');
  const [noteOriginalQty, setNoteOriginalQty] = useState('');
  const [noteActualQty, setNoteActualQty] = useState('');
  const [noteAmount, setNoteAmount] = useState('');
  const [noteDescription, setNoteDescription] = useState('');
  const [noteLinkedPoId, setNoteLinkedPoId] = useState('');
  const [linkedPOs, setLinkedPOs] = useState<{ id: string; number: string }[]>([]);

  // ── Fetch deals ───────────────────────────────────────────────────────

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get('/sales-orders?limit=100');
        const data = res.data.data?.salesOrders ?? res.data.data ?? [];
        // Show confirmed, processing, delivered, partial_shipped, shipped deals
        const active = data.filter((d: any) =>
          ['confirmed', 'processing', 'delivered', 'partial_shipped', 'shipped'].includes(d.status)
        );
        setDeals(active);
      } catch (err) {
        console.error('Failed to fetch deals', err);
      } finally {
        setLoadingDeals(false);
      }
    })();
  }, []);

  // ── Fetch payment data when deal changes ──────────────────────────────

  useEffect(() => {
    if (!selectedDealId) {
      setPaymentStages([]);
      setLinkedPOs([]);
      return;
    }
    (async () => {
      setLoadingPayments(true);
      try {
        const [payRes, poRes] = await Promise.all([
          api.get(`/deal-payments?salesOrderId=${selectedDealId}`).catch(() =>
            api.get('/deal-payments')
          ),
          api.get('/purchase-orders?limit=100').catch(() => ({ data: { data: [] } })),
        ]);

        // Filter payments for this SO
        const allStages = (payRes.data.data || []).map((s: any) => ({
          ...s,
          amount: Number(s.amount),
          paidAmount: Number(s.paidAmount),
          pendingAmount: Number(s.pendingAmount),
          payments: Array.isArray(s.payments) ? s.payments : [],
        }));

        const soStages = allStages.filter((s: PaymentStage) => s.salesOrderId === selectedDealId);
        setPaymentStages(soStages.length > 0 ? soStages : allStages.filter((s: PaymentStage) => s.salesOrderId === selectedDealId));

        // Find linked POs
        const poData = poRes.data.data?.purchaseOrders ?? poRes.data.data ?? [];
        const linked = poData
          .filter((po: any) => {
            const poSoIds = po.salesOrderIds ? JSON.parse(po.salesOrderIds || '[]') : [];
            return po.salesOrderId === selectedDealId || poSoIds.includes(selectedDealId);
          })
          .map((po: any) => ({ id: po.id, number: po.number }));
        setLinkedPOs(linked);

        // Pre-fill note quantities from SO
        const deal = deals.find(d => d.id === selectedDealId);
        if (deal?.items?.[0]) {
          setNoteOriginalQty(String(deal.items[0].quantity || ''));
        }
      } catch (err) {
        console.error('Failed to fetch payment data', err);
      } finally {
        setLoadingPayments(false);
      }
    })();
  }, [selectedDealId]);

  // ── Derived data ──────────────────────────────────────────────────────

  const deal = deals.find(d => d.id === selectedDealId);
  const quantity = deal?.items?.[0]?.quantity ?? 0;
  const sellRate = deal?.items?.[0]?.rate ?? 0;
  const sellTotal = Number(deal?.total) || 0;
  const commodity = deal?.items?.[0]?.description || deal?.config?.commodity || 'N/A';
  const buyerName = deal ? dealCustomerName(deal) : 'N/A';

  const totalPaid = paymentStages.reduce((sum, s) => sum + s.paidAmount, 0);
  const totalDue = paymentStages.reduce((sum, s) => sum + s.amount, 0);
  const totalPending = paymentStages.reduce((sum, s) => sum + s.pendingAmount, 0);

  const factoryStages = paymentStages.filter(s => s.type === 'factory');
  const buyerStages = paymentStages.filter(s => s.type === 'buyer');
  const transporterStages = paymentStages.filter(s => s.type === 'transporter');

  const config = deal?.config || {};
  const commodityCost = Number(config.commodityCost) || 0;
  const logisticsCost = Number(config.logisticsCost) || 0;
  const margin = sellTotal - commodityCost - logisticsCost;
  const marginPct = sellTotal > 0 ? (margin / sellTotal) * 100 : 0;

  // Check if all payments are completed
  const allPaymentsCompleted = paymentStages.length > 0 && paymentStages.every(s => s.status === 'completed');
  const hasPartialPayments = paymentStages.some(s => s.status === 'partial');

  // ── Handlers ──────────────────────────────────────────────────────────

  const handleCreateCreditNote = async () => {
    if (!noteAmount || !noteDescription) {
      alert('Please fill amount and description');
      return;
    }
    setCreatingNote(true);
    try {
      await api.post('/credit-notes', {
        salesOrderId: selectedDealId,
        purchaseOrderId: noteLinkedPoId || undefined,
        reason: noteReason,
        originalQuantity: Number(noteOriginalQty) || 0,
        actualQuantity: Number(noteActualQty) || 0,
        amount: Number(noteAmount),
        description: noteDescription,
        items: [{
          description: `${noteReason.replace('_', ' ')} adjustment — ${commodity}`,
          quantity: Math.abs((Number(noteOriginalQty) || 0) - (Number(noteActualQty) || 0)),
          rate: Number(noteAmount) / Math.max(1, Math.abs((Number(noteOriginalQty) || 0) - (Number(noteActualQty) || 0))),
          amount: Number(noteAmount),
        }],
      });
      alert('Credit note created successfully');
      resetNoteForm();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to create credit note';
      alert(msg);
    } finally {
      setCreatingNote(false);
    }
  };

  const handleCreateDebitNote = async () => {
    if (!noteAmount || !noteDescription) {
      alert('Please fill amount and description');
      return;
    }
    setCreatingNote(true);
    try {
      await api.post('/debit-notes', {
        salesOrderId: selectedDealId,
        purchaseOrderId: noteLinkedPoId || undefined,
        reason: noteReason,
        originalQuantity: Number(noteOriginalQty) || 0,
        actualQuantity: Number(noteActualQty) || 0,
        amount: Number(noteAmount),
        description: noteDescription,
        items: [{
          description: `${noteReason.replace('_', ' ')} adjustment — ${commodity}`,
          quantity: Math.abs((Number(noteOriginalQty) || 0) - (Number(noteActualQty) || 0)),
          rate: Number(noteAmount) / Math.max(1, Math.abs((Number(noteOriginalQty) || 0) - (Number(noteActualQty) || 0))),
          amount: Number(noteAmount),
        }],
      });
      alert('Debit note created successfully');
      resetNoteForm();
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to create debit note';
      alert(msg);
    } finally {
      setCreatingNote(false);
    }
  };

  const resetNoteForm = () => {
    setNoteReason('shortage');
    setNoteActualQty('');
    setNoteAmount('');
    setNoteDescription('');
    setNoteLinkedPoId('');
  };

  const handleCloseDeal = async () => {
    if (!selectedDealId) return;

    // Validation based on closure option
    if (closureOption === 'fully_paid' && !allPaymentsCompleted && paymentStages.length > 0) {
      const proceed = confirm(
        `Not all payment stages are marked as completed. Outstanding: ${formatINR(totalPending)}.\n\nAre you sure you want to close this deal?`
      );
      if (!proceed) return;
    }

    if (closureOption === 'partial_payment' && !partialAmount) {
      alert('Please enter the partial payment amount');
      return;
    }

    if ((closureOption === 'credit_note' || closureOption === 'debit_note') && !noteAmount) {
      alert('Please create the Credit/Debit Note first before closing the deal');
      return;
    }

    setClosing(true);
    try {
      // Record partial payment if applicable
      if (closureOption === 'partial_payment' && partialAmount) {
        // Find the first pending buyer stage to record payment against
        const pendingStage = buyerStages.find(s => s.status !== 'completed') || paymentStages.find(s => s.status !== 'completed');
        if (pendingStage) {
          await api.post(`/deal-payments/${pendingStage.id}/pay`, {
            amount: Number(partialAmount),
            mode: 'bank_transfer',
            utr: '',
            notes: partialNotes,
          });
        }
      }

      // Update deal status to SETTLED or CLOSED
      const targetStatus = closureOption === 'fully_paid' ? 'closed' : 'delivered';
      await api.patch(`/sales-orders/${selectedDealId}`, {
        status: targetStatus,
        config: {
          ...config,
          closureType: closureOption,
          closedAt: new Date().toISOString(),
          closureNotes: closureOption === 'partial_payment' ? partialNotes : undefined,
          outstandingBalance: closureOption === 'partial_payment' ? totalPending - Number(partialAmount || 0) : 0,
        },
      });

      alert(`Deal ${deal?.number} has been ${targetStatus === 'closed' ? 'closed' : 'updated'} successfully`);
      navigate('/veda-agro/deals');
    } catch (err: any) {
      const msg = err?.response?.data?.message || 'Failed to close deal';
      alert(msg);
    } finally {
      setClosing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────────────────

  if (loadingDeals) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
        <span className="ml-2 text-sm text-gray-500">Loading deals...</span>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Deal Closure</h1>
        <p className="text-sm text-gray-500">Reconcile payments, create CN/DN, and close deals</p>
      </div>

      {/* Deal Selector */}
      <div className="mb-6 rounded-xl border border-gray-200 bg-white p-4">
        <label className="mb-2 block text-sm font-semibold text-gray-700">Select Deal</label>
        <select
          value={selectedDealId}
          onChange={e => {
            setSelectedDealId(e.target.value);
            setClosureOption('fully_paid');
            resetNoteForm();
            setPartialAmount('');
            setPartialNotes('');
          }}
          className="w-full max-w-md rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm"
        >
          <option value="">-- Select an active deal --</option>
          {deals.map(d => (
            <option key={d.id} value={d.id}>
              {d.number} — {dealCustomerName(d)} — {d.items?.[0]?.description || 'N/A'} ({formatINR(d.total)})
            </option>
          ))}
        </select>
      </div>

      {loadingPayments && (
        <div className="flex items-center justify-center py-12">
          <Loader2 size={24} className="animate-spin text-[var(--saffron-600)]" />
          <span className="ml-2 text-sm text-gray-500">Loading payment data...</span>
        </div>
      )}

      {selectedDealId && deal && !loadingPayments && (
        <>
          {/* Deal Summary */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Deal Summary</h2>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
              <div>
                <p className="text-xs text-gray-500">SO Number</p>
                <p className="font-medium text-gray-900 text-sm">{deal.number}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Buyer</p>
                <p className="font-medium text-gray-900 text-sm">{buyerName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Commodity</p>
                <p className="font-medium text-gray-900 text-sm">{commodity}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Quantity (MT)</p>
                <p className="font-medium text-gray-900 text-sm">{quantity}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Total Value</p>
                <p className="font-medium text-gray-900 text-sm">{formatINR(sellTotal)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Margin</p>
                <p className={`font-medium text-sm ${margin >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {formatINR(margin)} ({marginPct.toFixed(1)}%)
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Status</p>
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                  deal.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                  deal.status === 'delivered' ? 'bg-teal-100 text-teal-700' :
                  'bg-blue-100 text-blue-700'
                }`}>
                  {deal.status.charAt(0).toUpperCase() + deal.status.slice(1).replace('_', ' ')}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Status Overview */}
          <div className="mb-6">
            <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-4">
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <DollarSign size={16} /> Total Due
                </div>
                <p className="text-2xl font-bold text-gray-900">{formatINR(totalDue)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <CheckCircle size={16} /> Paid
                </div>
                <p className="text-2xl font-bold text-green-600">{formatINR(totalPaid)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Clock size={16} /> Pending
                </div>
                <p className="text-2xl font-bold text-orange-600">{formatINR(totalPending)}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-4">
                <div className="flex items-center gap-2 text-sm text-gray-500">
                  <Scale size={16} /> Progress
                </div>
                <p className="text-2xl font-bold text-gray-900">
                  {totalDue > 0 ? Math.round((totalPaid / totalDue) * 100) : 0}%
                </p>
                <div className="mt-2 h-2 rounded-full bg-gray-200">
                  <div
                    className="h-2 rounded-full bg-green-500 transition-all"
                    style={{ width: `${totalDue > 0 ? Math.min(100, (totalPaid / totalDue) * 100) : 0}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Payment stages table */}
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="flex items-center gap-2 bg-gray-50 px-4 py-3 border-b">
                <CreditCard size={18} className="text-gray-500" />
                <h3 className="font-semibold text-gray-900">Payment Stages</h3>
                <span className="text-sm text-gray-500">({paymentStages.length} stages)</span>
              </div>

              {paymentStages.length === 0 ? (
                <div className="px-4 py-8 text-center text-gray-400">No payment stages recorded for this deal</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-gray-200 bg-gray-50 text-left">
                        <th className="px-4 py-3 font-medium text-gray-500">Type</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Stage</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Amount</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Paid</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Pending</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Due Date</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Status</th>
                        <th className="px-4 py-3 font-medium text-gray-500">Details</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {paymentStages.map(s => (
                        <>
                          <tr key={s.id} className="hover:bg-gray-50">
                            <td className="px-4 py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                                s.type === 'factory' ? 'bg-purple-100 text-purple-700' :
                                s.type === 'transporter' ? 'bg-orange-100 text-orange-700' :
                                'bg-blue-100 text-blue-700'
                              }`}>
                                {s.type.charAt(0).toUpperCase() + s.type.slice(1)}
                              </span>
                            </td>
                            <td className="px-4 py-3 text-gray-600 capitalize">{s.stage.replace(/_/g, ' ')}</td>
                            <td className="px-4 py-3 font-medium text-gray-900">{formatINR(s.amount)}</td>
                            <td className="px-4 py-3 text-green-600">{formatINR(s.paidAmount)}</td>
                            <td className="px-4 py-3 text-orange-600">{formatINR(s.pendingAmount)}</td>
                            <td className="px-4 py-3 text-gray-600">{s.dueDate ? formatDate(s.dueDate) : '—'}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                                s.status === 'completed' ? 'bg-green-100 text-green-700' :
                                s.status === 'partial' ? 'bg-yellow-100 text-yellow-700' :
                                s.status === 'overdue' ? 'bg-red-100 text-red-700' :
                                'bg-gray-100 text-gray-600'
                              }`}>{s.status}</span>
                            </td>
                            <td className="px-4 py-3">
                              {s.payments.length > 0 && (
                                <button
                                  onClick={() => setShowPaymentDetails(showPaymentDetails === s.id ? null : s.id)}
                                  className="text-[var(--ink-0)] hover:text-[var(--ink-0)] text-xs flex items-center gap-1"
                                >
                                  {showPaymentDetails === s.id ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
                                  {s.payments.length} txn{s.payments.length > 1 ? 's' : ''}
                                </button>
                              )}
                            </td>
                          </tr>
                          {/* Expanded payment transactions */}
                          {showPaymentDetails === s.id && s.payments.length > 0 && (
                            <tr key={`${s.id}-details`}>
                              <td colSpan={8} className="bg-gray-50 px-4 py-3">
                                <div className="ml-4 space-y-1">
                                  {s.payments.map((p, i) => (
                                    <div key={i} className="flex items-center gap-4 text-xs text-gray-600">
                                      <span className="font-medium">{formatINR(p.amount)}</span>
                                      <span>{p.mode.replace('_', ' ')}</span>
                                      {p.utr && <span className="text-gray-400">UTR: {p.utr}</span>}
                                      <span className="text-gray-400">{p.date ? formatDate(p.date) : ''}</span>
                                    </div>
                                  ))}
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Payment chain summary */}
              {paymentStages.length > 0 && (
                <div className="border-t bg-gray-50 px-4 py-3 grid grid-cols-3 gap-4 text-xs">
                  <div>
                    <span className="text-gray-500">Factory:</span>{' '}
                    <span className="font-medium text-green-600">{formatINR(factoryStages.reduce((s, p) => s + p.paidAmount, 0))} paid</span>
                    <span className="text-gray-400"> / {formatINR(factoryStages.reduce((s, p) => s + p.amount, 0))}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Buyer:</span>{' '}
                    <span className="font-medium text-green-600">{formatINR(buyerStages.reduce((s, p) => s + p.paidAmount, 0))} collected</span>
                    <span className="text-gray-400"> / {formatINR(buyerStages.reduce((s, p) => s + p.amount, 0))}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Transporter:</span>{' '}
                    <span className="font-medium text-green-600">{formatINR(transporterStages.reduce((s, p) => s + p.paidAmount, 0))} paid</span>
                    <span className="text-gray-400"> / {formatINR(transporterStages.reduce((s, p) => s + p.amount, 0))}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Closure Options */}
          <div className="mb-6 rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Closure Options</h2>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
              {/* Fully Paid */}
              <label className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                closureOption === 'fully_paid' ? 'border-green-500 bg-green-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="closure"
                  checked={closureOption === 'fully_paid'}
                  onChange={() => setClosureOption('fully_paid')}
                  className="sr-only"
                />
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle size={18} className={closureOption === 'fully_paid' ? 'text-green-600' : 'text-gray-400'} />
                  <span className={`font-medium ${closureOption === 'fully_paid' ? 'text-green-700' : 'text-gray-700'}`}>Fully Paid</span>
                </div>
                <p className="text-xs text-gray-500">All payments received. Close deal as settled.</p>
              </label>

              {/* Partial Payment */}
              <label className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                closureOption === 'partial_payment' ? 'border-yellow-500 bg-yellow-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="closure"
                  checked={closureOption === 'partial_payment'}
                  onChange={() => setClosureOption('partial_payment')}
                  className="sr-only"
                />
                <div className="flex items-center gap-2 mb-1">
                  <Clock size={18} className={closureOption === 'partial_payment' ? 'text-yellow-600' : 'text-gray-400'} />
                  <span className={`font-medium ${closureOption === 'partial_payment' ? 'text-yellow-700' : 'text-gray-700'}`}>Partial Payment</span>
                </div>
                <p className="text-xs text-gray-500">Record partial amount. Track aging balance.</p>
              </label>

              {/* Credit Note */}
              <label className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                closureOption === 'credit_note' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="closure"
                  checked={closureOption === 'credit_note'}
                  onChange={() => setClosureOption('credit_note')}
                  className="sr-only"
                />
                <div className="flex items-center gap-2 mb-1">
                  <FileText size={18} className={closureOption === 'credit_note' ? 'text-blue-600' : 'text-gray-400'} />
                  <span className={`font-medium ${closureOption === 'credit_note' ? 'text-blue-700' : 'text-gray-700'}`}>Credit Note</span>
                </div>
                <p className="text-xs text-gray-500">Shortage, quality issue, or price dispute.</p>
              </label>

              {/* Debit Note */}
              <label className={`cursor-pointer rounded-lg border-2 p-4 transition-colors ${
                closureOption === 'debit_note' ? 'border-red-500 bg-red-50' : 'border-gray-200 hover:border-gray-300'
              }`}>
                <input
                  type="radio"
                  name="closure"
                  checked={closureOption === 'debit_note'}
                  onChange={() => setClosureOption('debit_note')}
                  className="sr-only"
                />
                <div className="flex items-center gap-2 mb-1">
                  <AlertCircle size={18} className={closureOption === 'debit_note' ? 'text-red-600' : 'text-gray-400'} />
                  <span className={`font-medium ${closureOption === 'debit_note' ? 'text-red-700' : 'text-gray-700'}`}>Debit Note</span>
                </div>
                <p className="text-xs text-gray-500">Additional charges or vendor claim.</p>
              </label>
            </div>
          </div>

          {/* Partial Payment Form */}
          {closureOption === 'partial_payment' && (
            <div className="mb-6 rounded-xl border border-yellow-200 bg-yellow-50 p-6">
              <h3 className="mb-4 text-sm font-semibold text-yellow-800">Record Partial Payment</h3>
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Amount Received</label>
                  <input
                    type="number"
                    value={partialAmount}
                    onChange={e => setPartialAmount(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder={`Outstanding: ${formatINR(totalPending, false)}`}
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Pending Balance (after)</label>
                  <p className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-orange-600">
                    {formatINR(Math.max(0, totalPending - Number(partialAmount || 0)))}
                  </p>
                </div>
                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">Notes</label>
                  <textarea
                    value={partialNotes}
                    onChange={e => setPartialNotes(e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="Reason for partial payment, expected date for balance..."
                  />
                </div>
              </div>
              {Number(partialAmount) > 0 && (
                <div className="mt-4 rounded-lg bg-yellow-100 border border-yellow-300 p-3 text-xs text-yellow-800">
                  Aging balance of {formatINR(Math.max(0, totalPending - Number(partialAmount)))} will be tracked. Deal will remain in delivered status until fully settled.
                </div>
              )}
            </div>
          )}

          {/* Credit/Debit Note Form */}
          {(closureOption === 'credit_note' || closureOption === 'debit_note') && (
            <div className={`mb-6 rounded-xl border p-6 ${
              closureOption === 'credit_note' ? 'border-blue-200 bg-blue-50' : 'border-red-200 bg-red-50'
            }`}>
              <h3 className={`mb-4 text-sm font-semibold ${
                closureOption === 'credit_note' ? 'text-blue-800' : 'text-red-800'
              }`}>
                {closureOption === 'credit_note' ? 'Create Credit Note' : 'Create Debit Note'}
              </h3>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={noteReason}
                    onChange={e => setNoteReason(e.target.value as NoteReason)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="shortage">Shortage</option>
                    <option value="quality_issue">Quality Issue</option>
                    <option value="price_dispute">Price Dispute</option>
                    <option value="partial_delivery">Partial Delivery</option>
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Link to PO</label>
                  <select
                    value={noteLinkedPoId}
                    onChange={e => setNoteLinkedPoId(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                  >
                    <option value="">No linked PO</option>
                    {linkedPOs.map(po => (
                      <option key={po.id} value={po.id}>{po.number}</option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Original Quantity (MT)</label>
                  <input
                    type="number"
                    value={noteOriginalQty}
                    onChange={e => setNoteOriginalQty(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g., 500"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Actual Quantity (MT)</label>
                  <input
                    type="number"
                    value={noteActualQty}
                    onChange={e => setNoteActualQty(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g., 480"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Amount Adjustment <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={noteAmount}
                    onChange={e => setNoteAmount(e.target.value)}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder="e.g., 72000"
                  />
                </div>

                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">Shortfall</label>
                  <p className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600">
                    {Number(noteOriginalQty) && Number(noteActualQty)
                      ? `${(Number(noteOriginalQty) - Number(noteActualQty)).toFixed(2)} MT (${((Number(noteOriginalQty) - Number(noteActualQty)) / Number(noteOriginalQty) * 100).toFixed(1)}%)`
                      : '—'
                    }
                  </p>
                </div>

                <div className="sm:col-span-2">
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    Description <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={noteDescription}
                    onChange={e => setNoteDescription(e.target.value)}
                    rows={2}
                    className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm"
                    placeholder={
                      noteReason === 'shortage' ? 'Shortage of X MT during unloading at destination...' :
                      noteReason === 'quality_issue' ? 'NPK grade below specification. Lab report attached...' :
                      noteReason === 'price_dispute' ? 'Rate discrepancy per agreed contract terms...' :
                      'Partial delivery — remaining X MT to be delivered by...'
                    }
                  />
                </div>
              </div>

              <div className="mt-4 flex justify-end">
                <button
                  onClick={closureOption === 'credit_note' ? handleCreateCreditNote : handleCreateDebitNote}
                  disabled={creatingNote}
                  className={`flex items-center gap-2 rounded-lg px-4 py-2 text-sm font-medium text-white disabled:opacity-50 ${
                    closureOption === 'credit_note' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-red-600 hover:bg-red-700'
                  }`}
                >
                  {creatingNote ? <Loader2 size={16} className="animate-spin" /> : <Plus size={16} />}
                  Create {closureOption === 'credit_note' ? 'Credit Note' : 'Debit Note'}
                </button>
              </div>
            </div>
          )}

          {/* Close Deal Action */}
          <div className="rounded-xl border border-gray-200 bg-white p-6">
            <h2 className="mb-4 text-sm font-semibold text-gray-700 uppercase">Finalize Deal</h2>

            {/* Reconciliation check */}
            <div className={`mb-4 rounded-lg border p-4 ${
              allPaymentsCompleted
                ? 'bg-green-50 border-green-200'
                : hasPartialPayments
                  ? 'bg-yellow-50 border-yellow-200'
                  : 'bg-gray-50 border-gray-200'
            }`}>
              <div className="flex items-center gap-2 text-sm">
                {allPaymentsCompleted ? (
                  <>
                    <CheckCircle size={16} className="text-green-600" />
                    <span className="font-medium text-green-800">All payment stages completed. Ready to close.</span>
                  </>
                ) : paymentStages.length === 0 ? (
                  <>
                    <AlertCircle size={16} className="text-gray-500" />
                    <span className="text-gray-600">No payment stages recorded. You can still close the deal.</span>
                  </>
                ) : (
                  <>
                    <AlertCircle size={16} className="text-yellow-600" />
                    <span className="font-medium text-yellow-800">
                      Outstanding balance: {formatINR(totalPending)} across {paymentStages.filter(s => s.status !== 'completed').length} stage(s)
                    </span>
                  </>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-1 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50"
              >
                <ArrowLeft size={16} /> Back
              </button>
              <button
                onClick={handleCloseDeal}
                disabled={closing}
                className="flex items-center gap-2 px-6 py-2 bg-[var(--ink-0)] text-white rounded-lg text-sm font-medium hover:bg-[var(--ink-1)] disabled:opacity-50"
              >
                {closing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle size={16} />}
                {closureOption === 'fully_paid' ? 'Close Deal' :
                 closureOption === 'partial_payment' ? 'Record Payment & Update' :
                 closureOption === 'credit_note' ? 'Close Deal (with CN)' :
                 'Close Deal (with DN)'}
              </button>
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!selectedDealId && !loadingDeals && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-400">
          <Package size={48} className="mb-3" />
          <p className="text-sm">Select an active deal above to begin the closure process</p>
        </div>
      )}
    </div>
  );
}
